import { PlayerRef } from "@remotion/player";
import { getIcon } from "../../../../../../../../utils/get-icon";
import { ZoomSlider } from "../../zoom-slider";
import {
  ActionRowWrapper,
  Wrapper,
  InnerWrapper,
  ActionButton,
  FullscreenButton,
} from "./styled";
import { useCallback } from "react";
import { useTimelineContext } from "../../../context/use-timeline-context";
import { generateRandomId } from "../../../context/utils";

interface Props {
  playerRef: React.RefObject<PlayerRef | null>;
}

const DesktopView = ({ playerRef }: Props) => {
  const {
    tracks,
    fps,
    deleteItem,
    addItem,
    addTrack,
    setSelectedItem,
    selectedItem,
  } = useTimelineContext();

  const toggleFullscreen = useCallback(() => {
    if (!playerRef.current) {
      console.warn("Video container element not found");
      return;
    }

    try {
      if (!document.fullscreenElement) {
        playerRef.current?.requestFullscreen();
      } else {
        document.exitFullscreen();
      }
    } catch (error) {
      console.error("Fullscreen API error:", error);
    }
  }, [playerRef]);

  const clickSplitItem = useCallback(() => {
    if (!selectedItem || !playerRef.current) return;

    const currentFrame = playerRef.current.getCurrentFrame();

    let selectedItemData = null;
    let selectedItemTrackIndex = -1;

    for (let i = 0; i < tracks.length; i++) {
      const foundItem = tracks[i].items.find(
        (item) => item.id === selectedItem
      );
      if (foundItem) {
        selectedItemData = foundItem;
        selectedItemTrackIndex = i;
        break;
      }
    }

    if (!selectedItemData) {
      console.warn("Selected item not found in tracks");
      return;
    }

    const itemStartFrame = selectedItemData.from;
    const itemEndFrame = itemStartFrame + selectedItemData.durationInFrames;

    if (currentFrame <= itemStartFrame || currentFrame >= itemEndFrame) {
      console.warn("Cannot split: playhead is not within the selected item");
      return;
    }

    const splitOffset = currentFrame - itemStartFrame;

    const firstPartDuration = splitOffset;

    const secondPartDuration = selectedItemData.durationInFrames - splitOffset;

    const firstPartId = generateRandomId();
    const firstPart = {
      ...JSON.parse(JSON.stringify(selectedItemData)),
      id: firstPartId,
      durationInFrames: firstPartDuration,
      isDragging: false,
    };

    const secondPartId = generateRandomId();
    const secondPart = {
      ...JSON.parse(JSON.stringify(selectedItemData)),
      id: secondPartId,
      from: currentFrame,
      durationInFrames: secondPartDuration,
      isDragging: false,
    };

    if (
      selectedItemData.type === "video" &&
      "videoStartFromInSeconds" in selectedItemData
    ) {
      const originalStartSeconds = selectedItemData.videoStartFromInSeconds;
      const splitOffsetSeconds = splitOffset / fps;

      secondPart.videoStartFromInSeconds =
        originalStartSeconds + splitOffsetSeconds;
    }

    deleteItem(selectedItem);

    addItem(selectedItemTrackIndex, firstPart);

    addItem(selectedItemTrackIndex, secondPart);

    setSelectedItem(secondPartId);
  }, [
    selectedItem,
    playerRef,
    tracks,
    fps,
    deleteItem,
    addItem,
    setSelectedItem,
  ]);

  const clickDeleteItem = useCallback(() => {
    if (!selectedItem) return;
    deleteItem(selectedItem);
    setSelectedItem(null);
  }, [deleteItem, selectedItem, setSelectedItem]);

  const clickDuplicateItem = useCallback(() => {
    if (!selectedItem) return;

    let selectedItemData = null;
    let selectedItemTrackIndex = -1;

    for (let i = 0; i < tracks.length; i++) {
      const foundItem = tracks[i].items.find(
        (item) => item.id === selectedItem
      );
      if (foundItem) {
        selectedItemData = foundItem;
        selectedItemTrackIndex = i;
        break;
      }
    }

    if (!selectedItemData) {
      console.warn("Selected item not found in tracks");
      return;
    }

    const newItemId = generateRandomId();

    const duplicatedItem = {
      ...JSON.parse(JSON.stringify(selectedItemData)),
      id: newItemId,
      isDragging: false,
    };

    const currentTrack = tracks[selectedItemTrackIndex];
    const otherItemsInTrack = currentTrack.items.filter(
      (item) => item.id !== selectedItem
    );

    const targetFrom =
      selectedItemData.from + selectedItemData.durationInFrames;

    const hasOverlap = otherItemsInTrack.some((item) => {
      return (
        (targetFrom >= item.from &&
          targetFrom < item.from + item.durationInFrames) ||
        (targetFrom + duplicatedItem.durationInFrames > item.from &&
          targetFrom < item.from + item.durationInFrames)
      );
    });

    if (!hasOverlap) {
      duplicatedItem.from = targetFrom;

      addItem(selectedItemTrackIndex, duplicatedItem);
    } else {
      const newTrackId = generateRandomId();
      const newTrack = {
        id: newTrackId,
        name: `Track ${tracks.length + 1}`,
        items: [],
      };

      duplicatedItem.from = selectedItemData.from;

      addTrack(newTrack);

      setTimeout(() => {
        addItem(0, duplicatedItem);
      }, 100);
    }

    setSelectedItem(newItemId);
  }, [selectedItem, tracks, addItem, addTrack, setSelectedItem]);

  const clickNewTrack = useCallback(() => {
    addTrack({
      id: generateRandomId(),
      name: `Track ${tracks.length + 1}`,
      items: [],
    });
  }, [addTrack, tracks.length]);

  return (
    <ActionRowWrapper>
      <Wrapper>
        <InnerWrapper>
          <ActionButton onClick={clickNewTrack}>
            {getIcon("plus")} Nytt spor
          </ActionButton>
          <ActionButton disabled={!selectedItem} onClick={clickDeleteItem}>
            {getIcon("trash")} Slett
          </ActionButton>
          <ActionButton disabled={!selectedItem} onClick={clickSplitItem}>
            {getIcon("scissors")} Splitt opp
          </ActionButton>
          <ActionButton disabled={!selectedItem} onClick={clickDuplicateItem}>
            {getIcon("duplicate")} Dupliser
          </ActionButton>
        </InnerWrapper>
      </Wrapper>
      <Wrapper>
        <InnerWrapper>
          <ZoomSlider />
          <FullscreenButton onClick={toggleFullscreen}>
            {getIcon("expand")}
          </FullscreenButton>
        </InnerWrapper>
      </Wrapper>
    </ActionRowWrapper>
  );
};

export default DesktopView;
